<script setup>
import XlSkeleton from '../../xl/XlSkeleton.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useCreateOnArrival } from '../../../composables/useCreateOnArrival';
import { useUnsavedChanges } from '../../../composables/useUnsavedChanges';
import CharacterCounter from '../../communications/CharacterCounter.vue';
import CommsInfoBanner from '../../communications/CommsInfoBanner.vue';
import { COMMS_HINTS } from './hints';
import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlField from '../../xl/XlField.vue';
import XlTooltip from '../../xl/XlTooltip.vue';
import { TERM_TIPS } from '../../../helpers/termTips';
import { BLOCKS, FALLBACK_NOTE, MERGE_FIELDS, toPlainText, withSamples } from '../../communications/mergeFieldSamples';
import SafeRichText from '../../communications/SafeRichText.vue';
import { useRoute, useRouter } from 'vue-router';
import { estimateRecipients, listSegments } from '../../../api/segments';
import { listTemplates } from '../../../api/templates';
import { sendAdHocMessage } from '../../../api/adhoc';
import { getHistoryEntry } from '../../../api/history';
import { createDraft, deleteDraft, listDrafts, updateDraft } from '../../../api/drafts';


const SUBJECT_MAX = 100;
const PREHEADER_MAX = 150;
const BODY_MAX = 2000;
// Email is the only channel: chat went with Hub Announcements (XN-4389).
const CHANNELS = ['email'];

// Every field here is a plain ref, never persisted to a store or storage —
// ad hoc messages are explicitly not drafts, so leaving the tab (unmounting
// this component) is enough to lose whatever was typed.
const segments = ref([]);
const loadingSegments = ref(true);
const listError = ref('');

const selectedSegmentIds = ref([]);
const subject = ref('');
const preheader = ref('');
const subjectField = ref(null);
const bodyHtml = ref('');
const subjectCounter = ref(null);
const bodyCounter = ref(null);

const root = ref(null);
const snapshot = () => ({
  selectedSegmentIds: [...selectedSegmentIds.value],
  subject: subject.value,
  preheader: preheader.value,
  bodyHtml: bodyHtml.value,
});
const { settle, withoutGuard } = useUnsavedChanges({
  key: 'send-message',
  root,
  snapshot,
});

const estimating = ref(false);
import { useAuthStore } from '../../../stores/auth';
import { canAssignBrokers } from '../../../helpers/roles';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
// A broker sends within a segment, not to it: the count and the wording both
// have to say whose policyholders these are.
const sendsHubWide = computed(() => canAssignBrokers(auth.userData));

const drafts = ref([]);
// The draft this composer is currently editing, so saving twice replaces it
// rather than leaving a trail of near-identical copies.
const draftId = ref(null);
const savingDraft = ref(false);
const draftError = ref('');

const estimatedRecipients = ref(null);
const sending = ref(false);
const sendError = ref('');

// Read from CharacterCounter's own exposed state, never re-derived here —
// see the comment on CharacterCounter.vue's defineExpose.
const subjectOverLimit = computed(() => subjectCounter.value?.isOver ?? false);
const bodyOverLimit = computed(() => bodyCounter.value?.isOver ?? false);

// The estimate is the only thing standing between staff and a send that
// silently reaches nobody: a segment whose rule matches no active policy
// still passes every field-level check above.
const noRecipients = computed(() => !estimating.value && estimatedRecipients.value === 0);

const selectedSegmentNames = computed(() =>
  selectedSegmentIds.value
    .map((id) => segments.value.find((segment) => segment.id === id)?.name)
    .filter(Boolean)
);

const canSend = computed(
  () =>
    selectedSegmentIds.value.length > 0 &&
    subject.value.trim().length > 0 &&
    !subjectOverLimit.value &&
    bodyHtml.value.trim().length > 0 &&
    !bodyOverLimit.value
);

const recipientsCheck = computed(() => {
  if (estimating.value) return { ok: false, text: 'Counting who this reaches…' };
  if (selectedSegmentIds.value.length && !Number.isFinite(estimatedRecipients.value)) {
    return { ok: false, text: 'Could not count the audience — the send will resolve it either way' };
  }

  const count = estimatedRecipients.value ?? 0;
  const audience = sendsHubWide.value ? 'policyholders' : 'of your policyholders';

  return { ok: count > 0, text: `${count.toLocaleString('en-US')} ${audience} will receive this` };
});

const preSendChecks = computed(() => [
  { testid: 'check-recipients', ...recipientsCheck.value },
  { testid: 'check-channel', ok: true, text: 'Going out by email' },
  {
    testid: 'check-where',
    ok: true,
    text: sendsHubWide.value
      ? 'It lands in their Messages, under From the agency — not in their chat with their broker'
      : 'It arrives from the agency, listed under your name',
  },
  { testid: 'check-reply', ok: true, text: 'Nobody can reply to it. To ask something they write to their broker in chat' },
]);

// Nothing typed is nothing to keep.
const hasSomethingToSave = computed(() => Boolean(
  selectedSegmentIds.value.length
  || subject.value.trim()
  || preheader.value.trim()
  || bodyHtml.value.trim()
));

const draftLabel = (draft) => draft.subject?.trim() || 'Untitled draft';

const fetchDrafts = async () => {
  try {
    drafts.value = await listDrafts();
  } catch (e) {
    drafts.value = [];
  }
};

const saveDraft = async () => {
  if (!hasSomethingToSave.value || savingDraft.value) return;

  savingDraft.value = true;
  draftError.value = '';
  const savedState = snapshot();
  const payload = {
    segment_ids: selectedSegmentIds.value,
    channels: CHANNELS,
    subject: subject.value,
    preheader: preheader.value || null,
    body_html: bodyHtml.value,
  };

  try {
    const saved = draftId.value
      ? await updateDraft(draftId.value, payload)
      : await createDraft(payload);
    draftId.value = saved.id;
    settle(savedState);
    await fetchDrafts();
    window.loadNotification?.({ message: 'Saved as a draft' });
  } catch (e) {
    draftError.value = 'Could not save this draft. Please try again.';
  } finally {
    savingDraft.value = false;
  }
};

const openDraft = (draft) => {
  draftId.value = draft.id;
  selectedSegmentIds.value = [...(draft.segment_ids || [])];
  subject.value = draft.subject ?? '';
  preheader.value = draft.preheader ?? '';
  bodyHtml.value = draft.body_html ?? '';
  settle();
};

const discardDraft = async (draft) => {
  try {
    await deleteDraft(draft.id);
    if (draftId.value === draft.id) draftId.value = null;
    await fetchDrafts();
  } catch (e) {
    draftError.value = 'Could not delete this draft. Please try again.';
  }
};

const fetchSegments = async () => {
  loadingSegments.value = true;
  listError.value = '';
  try {
    segments.value = await listSegments();
  } catch (e) {
    listError.value = 'Could not load segments.';
  } finally {
    loadingSegments.value = false;
  }
};

// The deduped union across every selected segment (SegmentResolver's own
// policyholderIdsFor, via /estimate-recipients) — not a sum of independent
// per-segment estimates, which would overstate reach for any policyholder
// matched by more than one selected segment. This is the same resolution
// the real send performs, so the number shown here is exact at the moment
// of the call (suppression is still checked only at send time, so the
// actual sent_count can come in lower).
let estimateToken = 0;

const refreshEstimate = async () => {
  if (!selectedSegmentIds.value.length) {
    estimatedRecipients.value = null;
    estimating.value = false;

    return;
  }

  // Only the newest request may write the answer: segment chips toggle faster
  // than the round trip, and an earlier reply landing last would show a count
  // for an audience that is no longer selected.
  const token = ++estimateToken;
  estimating.value = true;
  try {
    const { recipient_count: recipientCount } = await estimateRecipients(selectedSegmentIds.value);
    if (token === estimateToken) estimatedRecipients.value = recipientCount;
  } catch (e) {
    if (token === estimateToken) estimatedRecipients.value = null;
  } finally {
    if (token === estimateToken) estimating.value = false;
  }
};

watch(selectedSegmentIds, refreshEstimate, { deep: true });

const toggleSegment = (id) => {
  selectedSegmentIds.value = selectedSegmentIds.value.includes(id)
    ? selectedSegmentIds.value.filter((s) => s !== id)
    : [...selectedSegmentIds.value, id];
};

const send = async () => {
  // Guarded here as well as on the button: the disabled attribute is
  // presentation, and this is the last point before an irreversible send.
  if (!canSend.value || noRecipients.value) return;

  sending.value = true;
  sendError.value = '';
  try {
    const sent = await sendAdHocMessage({
      segment_ids: selectedSegmentIds.value,
      channels: CHANNELS,
      subject: subject.value,
      preheader: preheader.value || null,
      body_html: bodyHtml.value,
    });
    if (draftId.value) {
      await deleteDraft(draftId.value).catch(() => {});
      draftId.value = null;
    }
    announceSent(sent);
    settle();
    // History is where a send is checked, so that is where it lands, with the
    // new row marked.
    router.replace({ query: { tab: 'history', sent: sent.id } });
  } catch (e) {
    sendError.value = 'Could not send this message. Please try again.';
  } finally {
    sending.value = false;
  }
};

const announceSent = ({ sent_count: count = 0, suppressed_count: suppressed, failed_channels: failed = [] }) => {
  const reached = `Sent to ${count.toLocaleString('en-US')} ${count === 1 ? 'policyholder' : 'policyholders'}`;

  if (failed.length) {
    window.loadNotification?.({
      message: `${reached}, but ${failed.join(' and ')} failed. What went out was not resent.`,
      error: true,
    });
    return;
  }

  window.loadNotification?.({ message: suppressed ? `${reached} · ${suppressed} suppressed` : reached });
};

// No cancel path here: the compose form has no Cancel — you simply navigate
// away — so only the focus half of the contract applies.
useCreateOnArrival('message', async () => {
  await nextTick();
  subjectField.value?.focus();
});

/**
 * Templates picks a standard one and sends the reader here to compose with it.
 * Loaded rather than passed, so the link works when pasted or reloaded.
 */
const prefillFromTemplate = async () => {
  const wanted = Number(route.query.template);
  if (!wanted) return;

  try {
    const template = (await listTemplates()).find((t) => t.id === wanted);
    if (!template) return;

    subject.value = template.subject ?? '';
    preheader.value = template.preheader ?? '';
    bodyHtml.value = toPlainText(template.body_html);
    settle();
  } catch {
    // Composing from scratch is a worse start, not a broken one.
  }
};

/**
 * History's "Send something similar". Only what still applies comes across: a
 * segment deleted since is left for the reader to choose again rather than
 * sent to blindly.
 */
const prefillFromHistory = async () => {
  const id = Number(route.query.similar);
  if (!id) return;

  try {
    const entry = await getHistoryEntry(id);

    subject.value = entry.subject ?? '';
    preheader.value = entry.preheader ?? '';
    bodyHtml.value = toPlainText(entry.body_html);
    selectedSegmentIds.value = (entry.segment_ids ?? []).filter((sid) => segments.value.some((s) => s.id === sid));
    settle();
  } catch {
    // Composing from scratch is a worse start, not a broken one.
  }

  await withoutGuard(() => router.replace({ query: { ...route.query, similar: undefined } }));
};

const bodyField = ref(null);

/**
 * Dropped where the cursor is, so a field can be placed mid-sentence. Written
 * back through v-model rather than into the element, or Vue would overwrite it
 * on the next render.
 */
const insertText = async (token) => {
  const el = bodyField.value?.querySelector('textarea');

  if (!el) {
    bodyHtml.value += token;
    return;
  }

  const at = el.selectionStart ?? bodyHtml.value.length;
  bodyHtml.value = bodyHtml.value.slice(0, at) + token + bodyHtml.value.slice(el.selectionEnd ?? at);

  await nextTick();
  el.focus();
  el.setSelectionRange(at + token.length, at + token.length);
};

const insertField = (field) => insertText(`{{${field}}}`);

// A block is only drawn when its token has a line of its own.
const insertBlock = (block) => insertText(`\n{{${block}}}\n`);

onMounted(async () => {
  fetchDrafts();
  prefillFromTemplate();
  await fetchSegments();
  await prefillFromHistory();
});
</script>

<template>
  <div ref="root" class="send-message-tab">
    <CommsInfoBanner :text="COMMS_HINTS.send" />

    <div class="send-message-tab__columns">
      <XlCard padded class="send-message-tab__compose">
        <div v-if="drafts.length" class="send-message-tab__fld send-message-tab__drafts" data-testid="drafts">
          <p class="send-message-tab__label">Drafts</p>
          <ul>
            <li v-for="draft in drafts" :key="draft.id" :class="{ 'is-open': draft.id === draftId }">
              <button
                type="button"
                class="send-message-tab__draft-open"
                :data-testid="`draft-${draft.id}`"
                @click="openDraft(draft)"
              >{{ draftLabel(draft) }}</button>
              <button
                type="button"
                class="send-message-tab__draft-discard"
                :aria-label="`Delete draft ${draftLabel(draft)}`"
                :data-testid="`draft-${draft.id}-delete`"
                @click="discardDraft(draft)"
              >×</button>
            </li>
          </ul>
        </div>

        <div class="send-message-tab__fld">
          <p class="send-message-tab__label">
            <XlTooltip :text="TERM_TIPS.Segments" placement="bottom" underline>Segments</XlTooltip> <span v-if="!sendsHubWide" class="send-message-tab__note">— only your own policyholders inside each one</span>
          </p>
          <p v-if="listError" role="alert" class="send-message-tab__status">{{ listError }}</p>
          <div v-else-if="loadingSegments" class="send-message-tab__chips" data-testid="send-segments-loading">
            <XlSkeleton v-for="w in [118, 92, 140, 104]" :key="w" :width="`${w}px`" height="32px" radius="99px" class="send-message-tab__chip-sk" />
          </div>
          <div v-else class="send-message-tab__chips" data-testid="send-segments">
            <button
              v-for="segment in segments"
              :key="segment.id"
              type="button"
              class="send-message-tab__tag"
              :class="{ 'is-on': selectedSegmentIds.includes(segment.id) }"
              :aria-pressed="selectedSegmentIds.includes(segment.id)"
              :data-testid="`send-segment-${segment.id}`"
              @click="toggleSegment(segment.id)"
            >
              <svg v-if="selectedSegmentIds.includes(segment.id)" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.8 12.3 4.2 4.2L18.2 7.8" /></svg>
              {{ segment.name }}
            </button>
          </div>
        </div>

        <div class="send-message-tab__fld">
          <XlField
            ref="subjectField"
            v-model="subject"
            data-testid="subject-input"
            label="Subject"
            placeholder="Your home insurance renews in 30 days"
            :maxlength="SUBJECT_MAX"
          >
            <template #counter>
              <CharacterCounter ref="subjectCounter" :value="subject" :max="SUBJECT_MAX" plain-text />
            </template>
          </XlField>
        </div>

        <div class="send-message-tab__fld">
          <XlField
            v-model="preheader"
            data-testid="preheader-input"
            label="Preview line"
            placeholder="Shown after the subject in most inboxes"
            :maxlength="PREHEADER_MAX"
          >
            <template #counter>
              <CharacterCounter :value="preheader" :max="PREHEADER_MAX" plain-text />
            </template>
          </XlField>
        </div>

        <div ref="bodyField" class="send-message-tab__fld">
          <XlField
            v-model="bodyHtml"
            type="textarea"
            data-testid="body-input"
            label="Message"
            placeholder="Hi {{first_name}}, …"
            rows="7"
            :maxlength="BODY_MAX"
          >
            <template #counter>
              <CharacterCounter ref="bodyCounter" :value="bodyHtml" :max="BODY_MAX" />
            </template>
          </XlField>
        </div>

        <div class="send-message-tab__fld">
          <p class="send-message-tab__label">Insert a personalised field</p>
          <div class="send-message-tab__fields">
            <XlButton
              v-for="mf in MERGE_FIELDS"
              :key="mf.field"
              variant="ghost"
              size="sm"
              :data-testid="`merge-field-${mf.field}`"
              :title="`If missing: ${mf.fallback}`"
              @click="insertField(mf.field)"
            >
              <template #icon>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6" /></svg>
              </template>
              {{ mf.label }}
            </XlButton>
          </div>
          <p class="send-message-tab__helper" data-testid="merge-field-fallbacks">
            Filled in with each person's own details as the message goes out. {{ FALLBACK_NOTE }}
          </p>
        </div>

        <div class="send-message-tab__fld">
          <p class="send-message-tab__label">Insert a block</p>
          <div class="send-message-tab__fields">
            <XlButton
              v-for="b in BLOCKS"
              :key="b.block"
              variant="ghost"
              size="sm"
              :data-testid="`merge-block-${b.block}`"
              @click="insertBlock(b.block)"
            >
              <template #icon>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6" /></svg>
              </template>
              {{ b.label }}
            </XlButton>
          </div>
          <p class="send-message-tab__helper">
            Goes in on a line of its own. The email shows the policy as a table and the buttons as buttons; the portal shows them as a list and links.
          </p>
        </div>
      </XlCard>

      <XlCard padded class="send-message-tab__preview">
        <b class="send-message-tab__title">Preview</b>
        <!-- The send path mails the stored body verbatim, so the preview renders exactly that. -->
        <div class="send-message-tab__paper" data-testid="send-preview">
          <b>{{ withSamples(subject) || 'Subject line' }}</b>
          <span v-if="preheader" class="send-message-tab__preheader" data-testid="send-preview-preheader">{{ withSamples(preheader) }}</span>
          <SafeRichText v-if="bodyHtml" class="send-message-tab__paper-body" data-testid="send-preview-body" :html="withSamples(bodyHtml)" />
          <div v-else class="send-message-tab__paper-body">Body</div>
        </div>
        <b class="send-message-tab__checks-title">Before you send</b>
        <div class="send-message-tab__checks">
          <div v-for="check in preSendChecks" :key="check.testid" class="send-message-tab__check">
            <span class="send-message-tab__ico" :class="check.ok ? 'is-ok' : 'is-warning'">
              <svg v-if="check.ok" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5.8 12.3 4.2 4.2L18.2 7.8" /></svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4.4 3.6 19.6h16.8z" /><path d="M12 10v3.8M12 16.8h.01" /></svg>
            </span>
            <div class="send-message-tab__check-text" :data-testid="check.testid">{{ check.text }}</div>
          </div>
        </div>

        <p v-if="noRecipients" class="send-message-tab__blocked" data-testid="send-no-recipients">
          Nothing would be sent. No active policy matches the selected segments right now — check the
          segment's rule, or whether policies have been loaded for this hub.
        </p>
        <p v-if="sendError" role="alert" class="send-message-tab__blocked" data-testid="send-error">{{ sendError }}</p>
        <p v-if="draftError" role="alert" class="send-message-tab__blocked" data-testid="draft-error">{{ draftError }}</p>

        <XlButton
          class="send-message-tab__send"
          data-testid="send-message"
          :disabled="!canSend || sending || estimating || noRecipients"
          @click="send"
        >{{ sending ? 'Sending…' : 'Send message' }}</XlButton>

        <!-- Never disabled by the checks: a draft is saved because the message is not ready. -->
        <XlButton
          class="send-message-tab__draft-save"
          data-testid="save-draft"
          variant="ghost"
          :disabled="!hasSomethingToSave || savingDraft"
          @click="saveDraft"
        >{{ savingDraft ? 'Saving…' : 'Save as draft' }}</XlButton>
      </XlCard>
    </div>
  </div>
</template>

<style scoped lang="scss">
.send-message-tab {
  background-color: inherit;
  color: var(--xl-color-primary-text);

  &__columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  &__fld {
    margin-bottom: 16px;

    &:last-child { margin-bottom: 0; }
  }

  &__label {
    margin: 0 0 6px;
    cursor: default;
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  &__note {
    font-weight: 400;
    color: var(--xl-color-outline-variant);
  }

  &__status, &__helper {
    margin: 8px 0 0;
    font-size: 12px;
    color: var(--xl-color-outline);
  }

  &__status { margin: 0 0 8px; }

  &__chips, &__fields { display: flex; flex-wrap: wrap; gap: 8px; }

  &__tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 7px;
    background: var(--xl-color-border-subtle);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    color: var(--xl-color-secondary-text);
    white-space: nowrap;
    cursor: pointer;

    &.is-on {
      background: var(--xl-color-primary-container);
      color: var(--xl-color-primary);
    }
  }

  &__title { font-size: 15px; }

  &__paper {
    margin-top: 14px;
    padding: 16px;
    border: 1px solid var(--xl-color-border);
    border-radius: var(--xl-radius-lg);
    background: var(--xl-color-surface);
    box-shadow: var(--xl-shadow-1);

    > b { display: block; margin-bottom: 10px; font-size: 15px; }
  }

  &__preheader {
    display: block;
    margin: -4px 0 10px;
    font-size: 13px;
    color: var(--xl-color-outline);
  }

  &__paper-body {
    font-size: 14px;
    line-height: 1.6;
    color: var(--xl-color-secondary-text);
    white-space: pre-wrap;
  }

  &__checks-title { font-size: 13.5px; }

  &__checks { margin-top: 10px; }

  &__check {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 7px 0;
    border-bottom: 1px solid var(--xl-color-border-subtle);

    &:last-child { border-bottom: 0; }
  }

  &__ico {
    flex: 0 0 42px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 13px;

    &.is-ok {
      background: var(--xl-color-success-container);
      color: var(--xl-color-success);
    }

    &.is-warning {
      background: var(--xl-color-warning-container);
      color: var(--xl-color-warning);
    }
  }

  &__check-text {
    flex: 1;
    min-width: 0;
    font-size: 13.5px;
    color: var(--xl-color-primary-text);
  }

  &__blocked {
    margin: 12px 0 0;
    font-size: 12.5px;
    color: var(--xl-color-error);
  }

  &__send { margin-top: 16px; width: 100%; }

  &__draft-save { margin-top: 9px; width: 100%; }

  &__drafts {
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    li {
      display: flex;
      align-items: center;
      border-radius: 7px;
      background: var(--xl-color-border-subtle);
      color: var(--xl-color-secondary-text);
      overflow: hidden;

      &.is-open {
        background: var(--xl-color-primary-container);
        color: var(--xl-color-primary);
      }
    }
  }

  &__draft-open, &__draft-discard {
    border: 0;
    background: none;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: inherit;
    cursor: pointer;
  }

  &__draft-open { height: 34px; padding: 0 4px 0 14px; }

  &__draft-discard {
    height: 34px;
    padding: 0 12px 0 6px;
    font-size: 15px;
    line-height: 1;
    color: var(--xl-color-outline);
  }
}

@media (max-width: 900px) {
  .send-message-tab__columns { grid-template-columns: minmax(0, 1fr); }
}

.send-message-tab__chip-sk { width: auto; flex: 0 0 auto; }
</style>
